// Modal: fill a `pending` intake form.
// The lot worker (anyone with sales/admin/owner role) edits the
// tier-1 / tier-2 / tier-3 responses and clicks Submit to advance
// the form to awaiting_review.
//
// Slice 2: when a matching template is supplied, renders per-item
// Tier-1 (Pass / N/A radio + optional reason + notes) and Tier-2
// (Suggest checkbox + notes) rows. Falls back to the Slice-1 JSON
// textarea path when the form's template_id no longer resolves
// (orphaned form, or pre-Slice-2 row).
//
// `max-h-[90vh] overflow-y-auto` per the panel-modal overflow
// convention (docs/runbooks/dev_sync_and_fix.md notes 86bcdf21 /
// 7a430d54 / 0986844c).

import { useMemo, useState } from "react";
import {
  fillFulfillmentIntakeForm,
  type FulfillmentIntakeForm,
  type FulfillmentIntakeTemplate,
} from "@/lib/fulfillment/fulfillmentApi";
import {
  coerceTemplateItems,
  type IntakeTemplateItem,
} from "@/lib/fulfillment/templateItem";
import {
  emptyTier1Response,
  emptyTier2Response,
  isTier1Complete,
  type Tier1Response,
  type Tier2Response,
} from "@/lib/fulfillment/intakeResponseShape";
import {
  buildTier1Map,
  buildTier2Map,
  FillTier1Section,
  FillTier2Section,
} from "./FulfillmentIntakeItemRows";

type Props = {
  form: FulfillmentIntakeForm;
  /** Resolved template (matched by form.template_id). null falls back to JSON textareas. */
  template: FulfillmentIntakeTemplate | null;
  onClose: () => void;
  onSaved: () => void;
};

function stringifyJson(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return "{}";
  }
}

function safeParseJson(raw: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(raw);
    if (v && typeof v === "object" && !Array.isArray(v)) return v as Record<string, unknown>;
    return null;
  } catch {
    return null;
  }
}

export function FulfillmentIntakeFormFillModal({
  form,
  template,
  onClose,
  onSaved,
}: Props) {
  const tier1Items = useMemo<IntakeTemplateItem[]>(
    () => (template ? coerceTemplateItems(template.tier_1_items) : []),
    [template]
  );
  const tier2Items = useMemo<IntakeTemplateItem[]>(
    () => (template ? coerceTemplateItems(template.tier_2_items) : []),
    [template]
  );
  const useStructured = !!template && (tier1Items.length > 0 || tier2Items.length > 0);

  const [tier1Map, setTier1Map] = useState<Record<string, Tier1Response>>(() =>
    buildTier1Map(tier1Items, form.tier_1_responses)
  );
  const [tier2Map, setTier2Map] = useState<Record<string, Tier2Response>>(() =>
    buildTier2Map(tier2Items, form.tier_2_responses)
  );
  const [tier1Text, setTier1Text] = useState(() => stringifyJson(form.tier_1_responses));
  const [tier2Text, setTier2Text] = useState(() => stringifyJson(form.tier_2_responses));
  const [tier3Text, setTier3Text] = useState(form.tier_3_response);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tier1ReadyForSubmit = isTier1Complete(tier1Items, tier1Map);

  function setTier1(itemId: string, patch: Partial<Tier1Response>) {
    setTier1Map((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] ?? emptyTier1Response()), ...patch },
    }));
  }

  function setTier2(itemId: string, patch: Partial<Tier2Response>) {
    setTier2Map((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] ?? emptyTier2Response()), ...patch },
    }));
  }

  async function save(submit: boolean) {
    setBusy(true);
    setError(null);

    let tier1Payload: Record<string, unknown>;
    let tier2Payload: Record<string, unknown>;

    if (useStructured) {
      tier1Payload = tier1Map;
      tier2Payload = tier2Map;
    } else {
      const t1 = safeParseJson(tier1Text);
      if (!t1) {
        setError("Tier 1 responses must be a JSON object");
        setBusy(false);
        return;
      }
      const t2 = safeParseJson(tier2Text);
      if (!t2) {
        setError("Tier 2 responses must be a JSON object");
        setBusy(false);
        return;
      }
      tier1Payload = t1;
      tier2Payload = t2;
    }

    const r = await fillFulfillmentIntakeForm({
      form_id: form.id,
      tier_1_responses: tier1Payload,
      tier_2_responses: tier2Payload,
      tier_3_response: tier3Text,
      submit,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/60 p-2 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-2xl border border-gray-200 bg-white p-5 shadow-xl dark:border-zinc-700 dark:bg-zinc-900 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-bold text-gray-900 dark:text-zinc-100">Fill intake form</h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
          Form id <code>{form.id.slice(0, 8)}</code> · status{" "}
          <code>{form.status}</code>
          {template ? (
            <>
              {" "}
              · template <code>{template.slug}</code>
            </>
          ) : (
            <>
              {" "}
              · <span className="text-amber-700 dark:text-amber-400">no matching template</span>
            </>
          )}
        </p>

        <div className="mt-4 space-y-4">
          {useStructured ? (
            <>
              <FillTier1Section items={tier1Items} responses={tier1Map} onChange={setTier1} />
              <FillTier2Section items={tier2Items} responses={tier2Map} onChange={setTier2} />
            </>
          ) : (
            <>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
                  Tier 1 — Inspection responses (JSON object)
                </span>
                <textarea
                  value={tier1Text}
                  onChange={(e) => setTier1Text(e.target.value)}
                  rows={6}
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 font-mono text-xs dark:border-zinc-700 dark:bg-zinc-950"
                  placeholder='{"item-roof": {"pass": true}}'
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
                  Tier 2 — Upgrade elections (JSON object)
                </span>
                <textarea
                  value={tier2Text}
                  onChange={(e) => setTier2Text(e.target.value)}
                  rows={4}
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 font-mono text-xs dark:border-zinc-700 dark:bg-zinc-950"
                  placeholder='{"item-paint": {"elected": true}}'
                />
              </label>
            </>
          )}

          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-zinc-300">
              Tier 3 — Free-form suggestions
            </span>
            <textarea
              value={tier3Text}
              onChange={(e) => setTier3Text(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-gray-300 bg-white p-2 text-sm dark:border-zinc-700 dark:bg-zinc-950"
            />
          </label>

          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
              {error}
            </div>
          ) : null}
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          {useStructured && !tier1ReadyForSubmit ? (
            <span className="mr-auto text-xs text-gray-500 dark:text-zinc-400">
              Answer every Tier-1 item before submitting.
            </span>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save(false)}
            disabled={busy}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-900"
          >
            {busy ? "Saving…" : "Save draft"}
          </button>
          <button
            type="button"
            onClick={() => void save(true)}
            disabled={busy || (useStructured && !tier1ReadyForSubmit)}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {busy ? "Submitting…" : "Submit for review"}
          </button>
        </div>
      </div>
    </div>
  );
}
